/**
 * Le impostazioni dell'ufficio.
 *
 * Non sono preferenze personali: valgono per tutti quelli che entrano, e per
 * questo vivono in banca dati e non in `localStorage`. Un file per contesto,
 * come gli altri — oggi ci sta dentro una cosa sola, e va bene così: la seconda
 * arriverà, e il posto dove metterla c'è già.
 *
 * **Le credenziali del server di posta non stanno qui.** Un utente e una
 * password che comandano un invio verso l'esterno sono ambiente, non
 * configurazione d'ufficio: finirebbero in una tabella che una schermata legge
 * e una risposta HTTP porta in giro. Qui c'è solo la decisione — se mandare, e
 * a chi.
 */
import { z } from 'zod';
/**
 * Se una segnalazione di fallato deve produrre anche una mail, e a chi.
 *
 * `mail: true` con l'elenco vuoto è rifiutato: sarebbe un interruttore acceso
 * che non fa niente, cioè la peggiore delle tre risposte possibili — chi lo
 * guarda crede che la mail parta. Meglio non farlo accendere.
 *
 * I doppioni sono rifiutati per la stessa ragione per cui non si accettano in
 * silenzio: due volte lo stesso indirizzo sono due copie della stessa mail, e
 * chi le riceve pensa a un guasto.
 */
export const zAvvisiFallati = z
    .object({
    mail: z.boolean(),
    destinatari: z.array(z.string().email()).readonly(),
})
    .readonly()
    .refine((v) => !v.mail || v.destinatari.length > 0, {
    message: 'Serve almeno un destinatario per accendere la mail.',
    path: ['destinatari'],
})
    .refine((v) => new Set(v.destinatari).size === v.destinatari.length, {
    message: 'Lo stesso indirizzo è scritto due volte.',
    path: ['destinatari'],
});
/**
 * Lo stesso schema in scrittura.
 *
 * Non è un alias per pigrizia: qui la lettura e la scrittura hanno davvero la
 * stessa forma — si rilegge esattamente quello che si è scritto — e dichiararne
 * due copie vorrebbe dire tenerle allineate a mano.
 */
export const zScriviAvvisiFallati = zAvvisiFallati;
/**
 * When a parcel goes by BRT instead of GLS: strictly above either threshold.
 *
 * Defaults (15 kg, 2 parcels) live in the API service, not here: the schema
 * only says what a valid value looks like.
 */
export const zSceltaCorriere = z
    .object({
    sogliaKg: z.number().positive().max(999.9),
    sogliaColli: z.number().int().min(1).max(99),
})
    .readonly();
/**
 * What GLS needs on every AddParcel and we do not know by ourselves
 * (MU162 «AddParcel-CWD»): porto F=franco / A=assegnato, collo 0=normale /
 * 4=plus, PDF A6 (default) or A5. `NoteSpedizione` is at most 40 characters
 * (MU162 §errors); `ServiziAccessori` is a string of digits, empty for none.
 */
export const zParametriGls = z
    .object({
    tipoPorto: z.enum(['F', 'A']),
    tipoCollo: z.enum(['0', '4']),
    formatoPdf: z.enum(['A6', 'A5']),
    serviziAccessori: z.string().regex(/^\d*$/),
    note: z.string().max(40),
})
    .readonly();
//# sourceMappingURL=settings.js.map